<script setup lang="ts">
import FinestAmountInput from '@/components/FinestAmountInput.vue'
import FinestCheckbox from '@/components/FinestCheckbox.vue'
import FinestCombobox from '@/components/FinestCombobox.vue'
import FinestInputNumber from '@/components/FinestInputNumber.vue'
import FinestInputText from '@/components/FinestInputText.vue'
import FinestSelect from '@/components/FinestSelect.vue'
import type { AmountUnit } from '@/utils/amount-unit'
import { userLeftTheField } from '@/utils/field-leave'
import { type FieldState } from '@/utils/field-state'
import { isNumericFieldType } from '@/utils/field-value'
import { primeInputFocus, primeInputState } from '@/utils/prime-input-classes'
import DatePicker from 'primevue/datepicker'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { InlineEditableField, InlineEditableSelectOption, InlineEditableValue } from './types'

const props = defineProps<{
  field: InlineEditableField
  value: InlineEditableValue | Date
  unit: AmountUnit
  referenceValue: number | null
  state: FieldState | null
}>()

const emit = defineEmits<{
  'update:value': [value: InlineEditableValue | Date]
  'update:unit': [unit: AmountUnit]
  commit: []
  revert: []
}>()

const { t, locale } = useI18n({ useScope: 'local' })

const interactive = computed(() => !props.field.disabled && !props.field.readonly)

const placeholder = computed(() => {
  if (props.state === 'reading') {
    return t('readingPlaceholder')
  }

  if (props.field.placeholder) {
    return props.field.placeholder
  }

  /* Auswahl und Datum brauchen ihren Hinweis immer · ein Textfeld nur, solange es fehlt. */
  if (props.field.type === 'select' || props.field.type === 'combobox') {
    return t('selectPlaceholder')
  }

  if (props.field.type === 'date') {
    return t('datePlaceholder')
  }

  return props.state === 'missing' ? t('missingPlaceholder') : undefined
})

const primeInputClass = computed(() => [primeInputFocus, primeInputState(props.state)])

const booleanValue = computed(() => (typeof props.value === 'boolean' ? props.value : null))

const comboboxOptions = computed(() =>
  (props.field.options ?? []).map((option) => ({ label: option.label, value: String(option.value ?? '') })),
)

function toggleBoolean(next: boolean | null): void {
  if (!interactive.value) {
    return
  }

  emit('update:value', next)
  emit('commit')
}

function selectUnit(next: AmountUnit): void {
  emit('update:unit', next)
  emit('commit')
}

function commitOnLeave(): void {
  if (!userLeftTheField()) {
    return
  }

  emit('commit')
}
</script>

<template>
  <FinestCheckbox
    v-if="field.type === 'boolean'"
    :model-value="booleanValue"
    :disabled="!interactive"
    :state="state"
    :aria-label="field.label"
    @update:model-value="toggleBoolean" />

  <FinestInputText
    v-else-if="field.type === 'text'"
    :model-value="value as string"
    :placeholder="placeholder"
    :aria-label="field.label"
    :disabled="!interactive"
    :state="state"
    class="w-full"
    @update:model-value="emit('update:value', $event ?? null)"
    @keydown.enter.prevent="emit('commit')"
    @keydown.esc.prevent="emit('revert')"
    @blur="commitOnLeave" />

  <Textarea
    v-else-if="field.type === 'textarea'"
    :model-value="value as string"
    :placeholder="placeholder"
    :aria-label="field.label"
    :disabled="!interactive"
    :pt="{ root: { class: primeInputClass } }"
    class="w-full"
    rows="3"
    auto-resize
    @update:model-value="emit('update:value', $event ?? null)"
    @keydown.esc.prevent="emit('revert')"
    @blur="commitOnLeave" />

  <FinestAmountInput
    v-else-if="field.type === 'amount'"
    :model-value="value as number"
    :unit="unit"
    :reference-value="referenceValue"
    :placeholder="placeholder"
    :aria-label="field.label"
    :disabled="!interactive"
    :state="state"
    class="w-full"
    @update:model-value="emit('update:value', $event)"
    @update:unit="selectUnit($event ?? 'percent')"
    @keydown.enter.prevent="emit('commit')"
    @keydown.esc.prevent="emit('revert')"
    @blur="commitOnLeave" />

  <FinestInputNumber
    v-else-if="isNumericFieldType(field.type)"
    :model-value="value as number"
    :placeholder="placeholder"
    :aria-label="field.label"
    :disabled="!interactive"
    :state="state"
    :mode="field.type === 'currency' ? 'currency' : 'decimal'"
    :locale="locale"
    :suffix="field.type === 'percent' ? ' %' : (field.suffix ?? '')"
    :prefix="field.type !== 'currency' ? (field.prefix ?? '') : ''"
    :min-fraction-digits="field.fractionDigits ?? (field.type === 'currency' ? 2 : 0)"
    :max-fraction-digits="field.fractionDigits ?? 2"
    :use-grouping="field.useGrouping ?? true"
    class="w-full"
    @update:model-value="emit('update:value', $event)"
    @keydown.enter.prevent="emit('commit')"
    @keydown.esc.prevent="emit('revert')"
    @blur="commitOnLeave" />

  <DatePicker
    v-else-if="field.type === 'date'"
    :model-value="(value as Date) ?? null"
    :placeholder="placeholder"
    :aria-label="field.label"
    :disabled="!interactive"
    :min-date="field.minDate"
    :pt="{ pcInputText: { root: { class: primeInputClass } } }"
    class="w-full"
    fluid
    show-icon
    icon-display="input"
    date-format="dd.mm.yy"
    @update:model-value="emit('update:value', ($event as Date) ?? null)"
    @date-select="emit('commit')"
    @hide="emit('commit')"
    @keydown.enter.prevent="emit('commit')"
    @keydown.esc.prevent="emit('revert')" />

  <FinestSelect
    v-else-if="field.type === 'select'"
    :model-value="(value ?? null) as InlineEditableSelectOption['value']"
    :options="field.options ?? []"
    :placeholder="placeholder"
    :aria-label="field.label"
    :disabled="!interactive"
    :state="state"
    class="w-full"
    @update:model-value="emit('update:value', $event)"
    @change="emit('commit')"
    @keydown.esc.prevent="emit('revert')" />

  <FinestCombobox
    v-else-if="field.type === 'combobox'"
    :model-value="value as string"
    :options="comboboxOptions"
    :placeholder="placeholder"
    :aria-label="field.label"
    :disabled="!interactive"
    :state="state"
    class="w-full"
    @update:model-value="emit('update:value', $event)"
    @change="emit('commit')" />
</template>

<i18n lang="json">
{
  "de": {
    "missingPlaceholder": "Bitte ausfüllen",
    "readingPlaceholder": "Kommt aus Dokument",
    "selectPlaceholder": "Bitte wählen",
    "datePlaceholder": "TT.MM.JJJJ"
  },
  "en": {
    "missingPlaceholder": "Please fill in",
    "readingPlaceholder": "Comes from a document",
    "selectPlaceholder": "Please select",
    "datePlaceholder": "DD.MM.YYYY"
  }
}
</i18n>
